import React, { useMemo } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import { Team } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/fetchTeams/types";
import { useTeamManagementPage } from "@/modules/administration/features/EmployeeDirectoryManagement/hooks/useTeamManagementPage";
import CreateTeamDrawer from "@/modules/administration/features/EmployeeDirectoryManagement/components/Team/CreateTeamDrawer/CreateTeamDrawer";
import EditTeamDrawer from "@/modules/administration/features/EmployeeDirectoryManagement/components/Team/EditTeamDrawer/EditTeamDrawer";
import DeleteTeamConfirmation from "@/modules/administration/features/EmployeeDirectoryManagement/components/Team/DeleteTeamConfirmation/DeleteTeamConfirmation";
import TeamActionMenu from "@/modules/administration/features/EmployeeDirectoryManagement/components/Team/TeamActionMenu/TeamActionMenu";

const TeamManagementPage: React.FC = () => {
  const {
    list,
    loading,
    error,
    loadingCreate,
    loadingUpdate,
    showCreateDrawer,
    editTarget,
    deleteTarget,
    openCreateDrawer,
    closeCreateDrawer,
    openEditDrawer,
    closeEditDrawer,
    openDeleteModal,
    closeDeleteModal,
    handleCreate,
    handleUpdate,
    handleDelete,
  } = useTeamManagementPage();

  const columns = useMemo<Column<Team>[]>(
    () => [
      { id: "name", header: "Name", accessorKey: "name" },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <TeamActionMenu
            onDelete={openDeleteModal}
            onEdit={openEditDrawer}
            team={row.original}
          />
        ),
      },
    ],
    [openEditDrawer, openDeleteModal],
  );

  return (
    <>
      <MainPageWrapper
        actions={<Button onClick={openCreateDrawer}>Create Team</Button>}
        error={error}
        title="Employee Directory — Teams"
      >
        <DataTable
          columns={columns}
          data={list}
          error={error}
          loading={loading}
          noDataMessage="No teams yet."
          onPageChange={() => undefined}
          pageIndex={0}
          pageSize={list.length || 10}
          rowKeyExtractor={(item) => item.uuid}
          totalCount={list.length}
        />
      </MainPageWrapper>

      <CreateTeamDrawer
        isOpen={showCreateDrawer}
        loading={loadingCreate}
        onClose={closeCreateDrawer}
        onSubmit={handleCreate}
      />

      <EditTeamDrawer
        loading={loadingUpdate}
        onClose={closeEditDrawer}
        onSubmit={handleUpdate}
        team={editTarget}
      />

      <DeleteTeamConfirmation
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        team={deleteTarget}
      />
    </>
  );
};

export default TeamManagementPage;
